export const OVERLAY_MODES = ['alive', 'all', 'top'];
export const OVERLAY_ANCHORS = [
    'top-left',
    'top-center',
    'top-right',
    'middle-left',
    'middle-center',
    'middle-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
];
export const OVERLAY_FLAG_SHAPES = ['circle', 'rounded', 'square'];
export const OVERLAY_ROW_ANIMATIONS = ['none', 'fade', 'slide', 'cascade'];
export const OVERLAY_TEXT_CASES = ['uppercase', 'capitalize', 'none'];

const DEFAULT_LAYOUT = {
    canvasWidth: 1920,
    canvasHeight: 1080,
    anchor: 'top-right',
    offsetX: 24,
    offsetY: 24,
    frameWidth: 619,
    frameHeight: 1032,
    frameRadius: 22,
    rowsStart: 36.4,
    rowStep: 6.245,
    rowHeight: 5.6,
    rankLeft: 9.1,
    rankWidth: 12.8,
    nameLeft: 24.5,
    nameWidth: 49,
    pointsLeft: 75.8,
    pointsWidth: 12.3,
};

const DEFAULT_DISPLAY = {
    showRank: true,
    showPoints: true,
    showAliveDot: true,
    uppercase: true,
    textCase: 'uppercase',
    dimDeadRows: true,
    deadOpacity: 0.6,
    fillEmptyRows: true,
    flagShape: 'circle',
    pointsDecimals: 0,
    rowAnimation: 'none',
};

const DEFAULT_THEME = {
    backgroundColor: 'transparent',
    backgroundImage: '',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
    fontFamily: 'OmnesBlack',
    fontFile: '',
    fontWeight: 900,
    letterSpacing: 0.025,
    rankColor: '#000000',
    nameColor: '#000000',
    pointsColor: '#000000',
    aliveNameColor: '',
    aliveColor: '#ff2626',
    aliveDotSize: 10,
    aliveDotGap: 9,
    aliveDotPulse: true,
    rankFontSize: 30,
    pointsFontSize: 25,
    nameFontRatio: 0.6,
    textShadow: 'none',
};

const DEFAULT_PAGINATION = {
    auto: true,
    intervalSeconds: 20,
};

export const DEFAULT_OVERLAY_CONFIG = {
    title: '',
    leaderboardId: '',
    mode: 'alive',
    pageSize: 10,
    maxEntries: 50,
    refreshSeconds: 10,
    aliveRefreshSeconds: 2,
    showFlags: false,
    customCss: '',
    pagination: DEFAULT_PAGINATION,
    layout: DEFAULT_LAYOUT,
    display: DEFAULT_DISPLAY,
    theme: DEFAULT_THEME,
};

export const OVERLAY_DEFAULTS = {
    layout: DEFAULT_LAYOUT,
    display: DEFAULT_DISPLAY,
    theme: DEFAULT_THEME,
    pagination: DEFAULT_PAGINATION,
};

function clampInteger(value, fallback, min, max) {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
}

function clampNumber(value, fallback, min, max) {
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
}

function nonEmptyString(value, fallback) {
    return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function optionalString(value, fallback = '') {
    return typeof value === 'string' ? value.trim() : fallback;
}

function normalizeBoolean(value, fallback) {
    return typeof value === 'boolean' ? value : fallback;
}

function oneOf(value, allowed, fallback) {
    const normalized = String(value || '').trim();
    return allowed.includes(normalized) ? normalized : fallback;
}

function plainObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

export function validateOverlaySlug(slug) {
    const normalized = String(slug || '').trim().toLowerCase();
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized)) {
        throw new Error('Le slug de l’overlay est invalide.');
    }
    return normalized;
}

export function resolveOverlayAssetUrl(assetPath, configUrl) {
    if (!assetPath) return '';
    try {
        return new URL(assetPath, configUrl).toString();
    } catch (error) {
        return '';
    }
}

export function normalizeOverlayConfig(rawConfig, { slug, configUrl } = {}) {
    if (!rawConfig || typeof rawConfig !== 'object' || Array.isArray(rawConfig)) {
        throw new Error('La configuration de l’overlay doit être un objet JSON.');
    }

    const normalizedSlug = validateOverlaySlug(slug);
    const leaderboardId = String(rawConfig.leaderboardId || '').trim();
    if (leaderboardId && !/^\d{6,30}$/.test(leaderboardId)) {
        throw new Error('Le champ leaderboardId doit contenir uniquement des chiffres, entre 6 et 30.');
    }

    const rawLayout = plainObject(rawConfig.layout);
    const rawDisplay = plainObject(rawConfig.display);
    const rawTheme = plainObject(rawConfig.theme);
    const rawPagination = plainObject(rawConfig.pagination);

    return {
        slug: normalizedSlug,
        title: optionalString(rawConfig.title),
        leaderboardId,
        mode: oneOf(rawConfig.mode, OVERLAY_MODES, DEFAULT_OVERLAY_CONFIG.mode),
        pageSize: clampInteger(rawConfig.pageSize, DEFAULT_OVERLAY_CONFIG.pageSize, 1, 40),
        maxEntries: clampInteger(rawConfig.maxEntries, DEFAULT_OVERLAY_CONFIG.maxEntries, 1, 500),
        refreshSeconds: clampInteger(rawConfig.refreshSeconds, DEFAULT_OVERLAY_CONFIG.refreshSeconds, 2, 300),
        aliveRefreshSeconds: clampInteger(
            rawConfig.aliveRefreshSeconds,
            DEFAULT_OVERLAY_CONFIG.aliveRefreshSeconds,
            1,
            120
        ),
        showFlags: normalizeBoolean(rawConfig.showFlags, DEFAULT_OVERLAY_CONFIG.showFlags),
        customCss: typeof rawConfig.customCss === 'string' ? rawConfig.customCss.slice(0, 20000) : '',
        pagination: {
            auto: normalizeBoolean(rawPagination.auto, DEFAULT_PAGINATION.auto),
            intervalSeconds: clampInteger(
                rawPagination.intervalSeconds,
                DEFAULT_PAGINATION.intervalSeconds,
                3,
                600
            ),
        },
        layout: {
            canvasWidth: clampInteger(rawLayout.canvasWidth, DEFAULT_LAYOUT.canvasWidth, 240, 3840),
            canvasHeight: clampInteger(rawLayout.canvasHeight, DEFAULT_LAYOUT.canvasHeight, 240, 2160),
            anchor: oneOf(rawLayout.anchor, OVERLAY_ANCHORS, DEFAULT_LAYOUT.anchor),
            offsetX: clampInteger(rawLayout.offsetX, DEFAULT_LAYOUT.offsetX, -2000, 2000),
            offsetY: clampInteger(rawLayout.offsetY, DEFAULT_LAYOUT.offsetY, -2000, 2000),
            frameWidth: clampInteger(rawLayout.frameWidth, DEFAULT_LAYOUT.frameWidth, 120, 3840),
            frameHeight: clampInteger(rawLayout.frameHeight, DEFAULT_LAYOUT.frameHeight, 120, 2160),
            frameRadius: clampInteger(rawLayout.frameRadius, DEFAULT_LAYOUT.frameRadius, 0, 120),
            rowsStart: clampNumber(rawLayout.rowsStart, DEFAULT_LAYOUT.rowsStart, 0, 100),
            rowStep: clampNumber(rawLayout.rowStep, DEFAULT_LAYOUT.rowStep, 0.5, 50),
            rowHeight: clampNumber(rawLayout.rowHeight, DEFAULT_LAYOUT.rowHeight, 0.5, 50),
            rankLeft: clampNumber(rawLayout.rankLeft, DEFAULT_LAYOUT.rankLeft, 0, 100),
            rankWidth: clampNumber(rawLayout.rankWidth, DEFAULT_LAYOUT.rankWidth, 0, 100),
            nameLeft: clampNumber(rawLayout.nameLeft, DEFAULT_LAYOUT.nameLeft, 0, 100),
            nameWidth: clampNumber(rawLayout.nameWidth, DEFAULT_LAYOUT.nameWidth, 1, 100),
            pointsLeft: clampNumber(rawLayout.pointsLeft, DEFAULT_LAYOUT.pointsLeft, 0, 100),
            pointsWidth: clampNumber(rawLayout.pointsWidth, DEFAULT_LAYOUT.pointsWidth, 0, 100),
        },
        display: {
            showRank: normalizeBoolean(rawDisplay.showRank, DEFAULT_DISPLAY.showRank),
            showPoints: normalizeBoolean(rawDisplay.showPoints, DEFAULT_DISPLAY.showPoints),
            showAliveDot: normalizeBoolean(rawDisplay.showAliveDot, DEFAULT_DISPLAY.showAliveDot),
            uppercase: normalizeBoolean(rawDisplay.uppercase, DEFAULT_DISPLAY.uppercase),
            textCase: oneOf(
                rawDisplay.textCase,
                OVERLAY_TEXT_CASES,
                rawDisplay.uppercase === false ? 'none' : DEFAULT_DISPLAY.textCase
            ),
            dimDeadRows: normalizeBoolean(rawDisplay.dimDeadRows, DEFAULT_DISPLAY.dimDeadRows),
            deadOpacity: clampNumber(rawDisplay.deadOpacity, DEFAULT_DISPLAY.deadOpacity, 0.05, 1),
            fillEmptyRows: normalizeBoolean(rawDisplay.fillEmptyRows, DEFAULT_DISPLAY.fillEmptyRows),
            flagShape: oneOf(rawDisplay.flagShape, OVERLAY_FLAG_SHAPES, DEFAULT_DISPLAY.flagShape),
            pointsDecimals: clampInteger(rawDisplay.pointsDecimals, DEFAULT_DISPLAY.pointsDecimals, 0, 3),
            rowAnimation: oneOf(rawDisplay.rowAnimation, OVERLAY_ROW_ANIMATIONS, DEFAULT_DISPLAY.rowAnimation),
        },
        theme: {
            backgroundColor: nonEmptyString(rawTheme.backgroundColor, DEFAULT_THEME.backgroundColor),
            backgroundImage: resolveOverlayAssetUrl(rawTheme.backgroundImage, configUrl),
            backgroundPosition: nonEmptyString(rawTheme.backgroundPosition, DEFAULT_THEME.backgroundPosition),
            backgroundSize: nonEmptyString(rawTheme.backgroundSize, DEFAULT_THEME.backgroundSize),
            fontFamily: nonEmptyString(rawTheme.fontFamily, DEFAULT_THEME.fontFamily),
            fontFile: resolveOverlayAssetUrl(rawTheme.fontFile, configUrl),
            fontWeight: clampInteger(rawTheme.fontWeight, DEFAULT_THEME.fontWeight, 100, 900),
            letterSpacing: clampNumber(rawTheme.letterSpacing, DEFAULT_THEME.letterSpacing, -0.1, 0.4),
            rankColor: nonEmptyString(rawTheme.rankColor, DEFAULT_THEME.rankColor),
            nameColor: nonEmptyString(rawTheme.nameColor, DEFAULT_THEME.nameColor),
            pointsColor: nonEmptyString(rawTheme.pointsColor, DEFAULT_THEME.pointsColor),
            aliveNameColor: optionalString(rawTheme.aliveNameColor),
            aliveColor: nonEmptyString(rawTheme.aliveColor, DEFAULT_THEME.aliveColor),
            aliveDotSize: clampNumber(rawTheme.aliveDotSize, DEFAULT_THEME.aliveDotSize, 2, 60),
            aliveDotGap: clampNumber(rawTheme.aliveDotGap, DEFAULT_THEME.aliveDotGap, 0, 60),
            aliveDotPulse: normalizeBoolean(rawTheme.aliveDotPulse, DEFAULT_THEME.aliveDotPulse),
            rankFontSize: clampInteger(rawTheme.rankFontSize, DEFAULT_THEME.rankFontSize, 6, 120),
            pointsFontSize: clampInteger(rawTheme.pointsFontSize, DEFAULT_THEME.pointsFontSize, 6, 120),
            nameFontRatio: clampNumber(rawTheme.nameFontRatio, DEFAULT_THEME.nameFontRatio, 0.2, 1.4),
            textShadow: optionalString(rawTheme.textShadow, DEFAULT_THEME.textShadow) || 'none',
        },
    };
}

export function resolveOverlayMode(searchParams, configMode) {
    const requestedMode = searchParams.get('mode');
    if (OVERLAY_MODES.includes(requestedMode)) return requestedMode;
    const alive = searchParams.get('alive');
    if (alive === 'true' || alive === '1') return 'alive';
    if (alive === 'false' || alive === '0') return 'all';
    return OVERLAY_MODES.includes(configMode) ? configMode : DEFAULT_OVERLAY_CONFIG.mode;
}

export function getOverlayConfigUrl(slug, pageUrl = window.location.href) {
    const normalizedSlug = validateOverlaySlug(slug);
    const publicRoot = process.env.PUBLIC_URL || '/';
    const rootUrl = new URL(publicRoot.endsWith('/') ? publicRoot : `${publicRoot}/`, pageUrl);
    return new URL(`overlays/${normalizedSlug}/config.json`, rootUrl).toString();
}

export async function loadOverlayConfig(slug, { signal, pageUrl } = {}) {
    const configUrl = getOverlayConfigUrl(slug, pageUrl);
    const response = await fetch(configUrl, { signal, cache: 'no-store' });
    if (!response.ok) {
        if (response.status === 404) {
            throw new Error(`L’overlay « ${slug} » n'existe pas.`);
        }
        throw new Error(`Impossible de charger la configuration (${response.status}).`);
    }

    const rawConfig = await response.json();
    return normalizeOverlayConfig(rawConfig, { slug, configUrl: response.url || configUrl });
}
